import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Header } from "@/components/Header";
import { supabase } from "@/integrations/supabase/client";
import { useLocale } from "@/i18n/locale-context";
import {
  getStoredConsent,
  type GdprConsent,
} from "@/components/GdprConsentModal";

const STORAGE_KEY = "symmetria.gdpr.consent.v1";
const RETENTION_KEY = "glowup.pro.retention.v1";

type RetentionDays = 30 | 90 | 180 | 365 | 0; // 0 = keep until manual deletion
type RetentionPrefs = {
  retentionDays: RetentionDays;
  euOnly: boolean;
  updatedAt: string;
};

const DEFAULT_RETENTION: RetentionPrefs = {
  retentionDays: 90,
  euOnly: true,
  updatedAt: new Date(0).toISOString(),
};

function readRetention(): RetentionPrefs {
  if (typeof window === "undefined") return DEFAULT_RETENTION;
  try {
    const raw = window.localStorage.getItem(RETENTION_KEY);
    if (!raw) return DEFAULT_RETENTION;
    const parsed = JSON.parse(raw) as Partial<RetentionPrefs>;
    const allowed: RetentionDays[] = [0, 30, 90, 180, 365];
    const d = (parsed.retentionDays ?? DEFAULT_RETENTION.retentionDays) as RetentionDays;
    return {
      retentionDays: allowed.includes(d) ? d : DEFAULT_RETENTION.retentionDays,
      euOnly: parsed.euOnly !== false,
      updatedAt: parsed.updatedAt ?? DEFAULT_RETENTION.updatedAt,
    };
  } catch {
    return DEFAULT_RETENTION;
  }
}

const COPY = {
  fr: {
    head: "Paramètres — Glow Up Coach",
    kicker: "Mon compte",
    title: "Paramètres",
    subtitle: "Mettez à jour vos informations et gérez vos consentements à tout moment.",
    profile: "Profil",
    name: "Prénom / pseudo",
    namePh: "Comment souhaitez-vous être appelé·e ?",
    email: "Adresse e-mail",
    emailHint: "Un e-mail de confirmation sera envoyé à la nouvelle adresse.",
    save: "Enregistrer",
    saving: "Enregistrement…",
    security: "Sécurité",
    newPwd: "Nouveau mot de passe",
    newPwdPh: "Au moins 8 caractères",
    updatePwd: "Mettre à jour le mot de passe",
    consents: "Consentements RGPD",
    consentsDesc: "Vous pouvez modifier vos préférences à tout moment. Ces choix sont stockés localement sur cet appareil.",
    storePhotos: "Conserver mes selfies dans mon historique",
    storePhotosDesc: "Vos photos sont chiffrées et stockées en UE. Désactiver supprimera les nouveaux selfies après analyse — l'historique existant n'est pas affecté.",
    improveAi: "Aider à améliorer l'IA (anonymisé)",
    improveAiDesc: "Vos analyses pourront être utilisées de façon anonyme pour améliorer le modèle.",
    saved: "Modifications enregistrées",
    pwdShort: "Le mot de passe doit contenir au moins 8 caractères.",
    pwdSaved: "Mot de passe mis à jour",
    back: "← Retour",
    settings: "Paramètres",
    advanced: "Confidentialité avancée & suppression du compte",
    retention: "Confidentialité Pro & rétention des photos",
    retentionDesc: "Choisissez combien de temps vos photos liées aux protocoles Pro sont conservées et où elles sont traitées. Ces réglages s'appliquent aux nouvelles captures.",
    retentionPeriod: "Durée de conservation",
    retentionHint: "Au-delà de cette durée, les photos sont supprimées automatiquement. Les analyses (scores, conseils) sont conservées sans la photo.",
    eu: "Traitement & hébergement en Union européenne uniquement",
    euDesc: "Stockage chiffré dans l'UE et appels IA via fournisseurs avec garanties UE/SCC. Désactiver n'est pas recommandé.",
    days30: "30 jours",
    days90: "90 jours (recommandé)",
    days180: "180 jours",
    days365: "1 an",
    daysKeep: "Jusqu'à suppression manuelle",
    lastUpdated: "Dernière mise à jour",
  },
  en: {
    head: "Settings — Glow Up Coach",
    kicker: "My account",
    title: "Settings",
    subtitle: "Update your details and manage your consents anytime.",
    profile: "Profile",
    name: "First name / nickname",
    namePh: "How should we call you?",
    email: "Email address",
    emailHint: "A confirmation email will be sent to the new address.",
    save: "Save",
    saving: "Saving…",
    security: "Security",
    newPwd: "New password",
    newPwdPh: "At least 8 characters",
    updatePwd: "Update password",
    consents: "GDPR consents",
    consentsDesc: "You can change your preferences at any time. These choices are stored locally on this device.",
    storePhotos: "Keep my selfies in my history",
    storePhotosDesc: "Your photos are encrypted and stored in the EU. Turning this off will discard new selfies after analysis — existing history is unaffected.",
    improveAi: "Help improve the AI (anonymous)",
    improveAiDesc: "Your analyses may be used anonymously to improve the model.",
    saved: "Changes saved",
    pwdShort: "Password must be at least 8 characters.",
    pwdSaved: "Password updated",
    back: "← Back",
    settings: "Settings",
    advanced: "Advanced privacy & account deletion",
    retention: "Pro privacy & photo retention",
    retentionDesc: "Choose how long your Pro protocol photos are kept and where they are processed. These settings apply to new captures.",
    retentionPeriod: "Retention period",
    retentionHint: "After this period, photos are deleted automatically. Analyses (scores, tips) are kept without the photo.",
    eu: "Process & host in the European Union only",
    euDesc: "Encrypted storage in the EU and AI calls via providers with EU/SCC guarantees. Disabling is not recommended.",
    days30: "30 days",
    days90: "90 days (recommended)",
    days180: "180 days",
    days365: "1 year",
    daysKeep: "Keep until I delete",
    lastUpdated: "Last updated",
  },
  nl: {
    head: "Instellingen — Glow Up Coach",
    kicker: "Mijn account",
    title: "Instellingen",
    subtitle: "Werk je gegevens bij en beheer je toestemmingen op elk moment.",
    profile: "Profiel",
    name: "Voornaam / bijnaam",
    namePh: "Hoe mogen we je noemen?",
    email: "E-mailadres",
    emailHint: "Er wordt een bevestigingsmail naar het nieuwe adres gestuurd.",
    save: "Opslaan",
    saving: "Opslaan…",
    security: "Beveiliging",
    newPwd: "Nieuw wachtwoord",
    newPwdPh: "Minstens 8 tekens",
    updatePwd: "Wachtwoord bijwerken",
    consents: "AVG-toestemmingen",
    consentsDesc: "Je kunt je voorkeuren op elk moment wijzigen. Deze keuzes worden lokaal opgeslagen op dit apparaat.",
    storePhotos: "Bewaar mijn selfies in mijn geschiedenis",
    storePhotosDesc: "Je foto's worden versleuteld en opgeslagen in de EU. Uitschakelen verwijdert nieuwe selfies na analyse — bestaande geschiedenis blijft.",
    improveAi: "Help de AI verbeteren (anoniem)",
    improveAiDesc: "Je analyses kunnen anoniem worden gebruikt om het model te verbeteren.",
    saved: "Wijzigingen opgeslagen",
    pwdShort: "Wachtwoord moet minstens 8 tekens bevatten.",
    pwdSaved: "Wachtwoord bijgewerkt",
    back: "← Terug",
    settings: "Instellingen",
    advanced: "Geavanceerde privacy & accountverwijdering",
    retention: "Pro-privacy & fotobewaring",
    retentionDesc: "Kies hoe lang je Pro-protocolfoto's worden bewaard en waar ze worden verwerkt. Deze instellingen gelden voor nieuwe opnames.",
    retentionPeriod: "Bewaartermijn",
    retentionHint: "Na deze termijn worden foto's automatisch verwijderd. Analyses (scores, tips) blijven bewaard zonder de foto.",
    eu: "Verwerking & hosting uitsluitend in de Europese Unie",
    euDesc: "Versleutelde opslag in de EU en AI-aanroepen via leveranciers met EU/SCC-garanties. Uitschakelen wordt afgeraden.",
    days30: "30 dagen",
    days90: "90 dagen (aanbevolen)",
    days180: "180 dagen",
    days365: "1 jaar",
    daysKeep: "Bewaren tot ik verwijder",
    lastUpdated: "Laatst bijgewerkt",
  },
};

export const Route = createFileRoute("/settings")({
  head: () => ({
    meta: [
      { title: "Paramètres — Glow Up Coach" },
      { name: "robots", content: "noindex" },
    ],
  }),
  component: SettingsPage,
});

function SettingsPage() {
  const { locale } = useLocale();
  const c = COPY[locale];
  const nav = useNavigate();

  const [authed, setAuthed] = useState(false);
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [pwd, setPwd] = useState("");
  const [savingProfile, setSavingProfile] = useState(false);
  const [savingPwd, setSavingPwd] = useState(false);
  const [storePhotos, setStorePhotos] = useState(false);
  const [improveAi, setImproveAi] = useState(false);
  const [retention, setRetention] = useState<RetentionPrefs>(DEFAULT_RETENTION);

  useEffect(() => {
    supabase.auth.getSession().then(({ data }) => {
      if (!data.session) {
        nav({ to: "/login" });
        return;
      }
      setAuthed(true);
      const u = data.session.user;
      setEmail(u.email ?? "");
      const meta = (u.user_metadata ?? {}) as { display_name?: string; full_name?: string };
      setName(meta.display_name ?? meta.full_name ?? "");
      const stored = getStoredConsent();
      if (stored) {
        setStorePhotos(stored.storePhotos);
        setImproveAi(stored.improveAi);
      }
      setRetention(readRetention());
    });
  }, [nav]);

  async function saveProfile() {
    setSavingProfile(true);
    try {
      const { data: sess } = await supabase.auth.getSession();
      const currentEmail = sess.session?.user.email ?? "";
      const payload: { email?: string; data?: Record<string, unknown> } = {
        data: { display_name: name.trim() },
      };
      if (email.trim() && email.trim().toLowerCase() !== currentEmail.toLowerCase()) {
        payload.email = email.trim();
      }
      const { error } = await supabase.auth.updateUser(payload);
      if (error) throw error;
      toast.success(c.saved);
    } catch (e: unknown) {
      toast.error(e instanceof Error ? e.message : "Error");
    } finally {
      setSavingProfile(false);
    }
  }

  async function updatePassword() {
    if (pwd.length < 8) {
      toast.error(c.pwdShort);
      return;
    }
    setSavingPwd(true);
    try {
      const { error } = await supabase.auth.updateUser({ password: pwd });
      if (error) throw error;
      setPwd("");
      toast.success(c.pwdSaved);
    } catch (e: unknown) {
      toast.error(e instanceof Error ? e.message : "Error");
    } finally {
      setSavingPwd(false);
    }
  }

  function persistConsents(next: { storePhotos: boolean; improveAi: boolean }) {
    setStorePhotos(next.storePhotos);
    setImproveAi(next.improveAi);
    try {
      const existing = getStoredConsent();
      const consent: GdprConsent = {
        storePhotos: next.storePhotos,
        improveAi: next.improveAi,
        acceptedAt: existing?.acceptedAt ?? new Date().toISOString(),
        version: 1,
      };
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(consent));
      toast.success(c.saved);
    } catch {}
  }

  function persistRetention(next: Partial<RetentionPrefs>) {
    const merged: RetentionPrefs = {
      ...retention,
      ...next,
      updatedAt: new Date().toISOString(),
    };
    setRetention(merged);
    try {
      window.localStorage.setItem(RETENTION_KEY, JSON.stringify(merged));
      toast.success(c.saved);
    } catch {}
  }

  if (!authed) return null;

  return (
    <div className="min-h-screen bg-background text-foreground" style={{ fontFamily: "var(--font-sans)" }}>
      <Header />
      <section className="max-w-2xl mx-auto px-6 py-16">
        <span className="text-xs font-bold uppercase tracking-[0.2em] text-accent">{c.kicker}</span>
        <h1 className="font-display font-bold text-4xl md:text-5xl mt-2 mb-3 tracking-tight" style={{ fontFamily: "var(--font-display)" }}>
          {c.title}
        </h1>
        <p className="text-muted-foreground mb-10">{c.subtitle}</p>

        <div className="rounded-3xl border border-foreground/10 p-6 md:p-8 mb-6">
          <h2 className="font-display font-bold text-xl mb-4" style={{ fontFamily: "var(--font-display)" }}>{c.profile}</h2>
          <div className="space-y-4">
            <div>
              <label className="text-sm font-semibold block mb-1.5">{c.name}</label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder={c.namePh}
                maxLength={80}
                className="w-full px-4 py-3 rounded-xl border border-foreground/20 bg-background text-sm focus:outline-none focus:ring-2 focus:ring-accent"
              />
            </div>
            <div>
              <label className="text-sm font-semibold block mb-1.5">{c.email}</label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                maxLength={255}
                className="w-full px-4 py-3 rounded-xl border border-foreground/20 bg-background text-sm focus:outline-none focus:ring-2 focus:ring-accent"
              />
              <p className="text-xs text-muted-foreground mt-1.5">{c.emailHint}</p>
            </div>
            <button
              onClick={saveProfile}
              disabled={savingProfile}
              className="bg-foreground text-background px-5 py-2.5 rounded-full text-sm font-bold hover:opacity-90 disabled:opacity-40"
            >
              {savingProfile ? c.saving : c.save}
            </button>
          </div>
        </div>

        <div className="rounded-3xl border border-foreground/10 p-6 md:p-8 mb-6">
          <h2 className="font-display font-bold text-xl mb-4" style={{ fontFamily: "var(--font-display)" }}>{c.security}</h2>
          <div className="space-y-4">
            <div>
              <label className="text-sm font-semibold block mb-1.5">{c.newPwd}</label>
              <input
                type="password"
                value={pwd}
                onChange={(e) => setPwd(e.target.value)}
                placeholder={c.newPwdPh}
                minLength={8}
                maxLength={128}
                autoComplete="new-password"
                className="w-full px-4 py-3 rounded-xl border border-foreground/20 bg-background text-sm focus:outline-none focus:ring-2 focus:ring-accent"
              />
            </div>
            <button
              onClick={updatePassword}
              disabled={savingPwd || pwd.length < 8}
              className="bg-foreground text-background px-5 py-2.5 rounded-full text-sm font-bold hover:opacity-90 disabled:opacity-40"
            >
              {savingPwd ? c.saving : c.updatePwd}
            </button>
          </div>
        </div>

        <div id="consents" className="rounded-3xl border border-foreground/10 p-6 md:p-8 mb-6">
          <h2 className="font-display font-bold text-xl mb-2" style={{ fontFamily: "var(--font-display)" }}>{c.consents}</h2>
          <p className="text-sm text-muted-foreground mb-5">{c.consentsDesc}</p>
          <div className="space-y-3">
            <label className="flex gap-3 p-4 rounded-2xl border border-foreground/10 cursor-pointer">
              <input
                type="checkbox"
                checked={storePhotos}
                onChange={(e) => persistConsents({ storePhotos: e.target.checked, improveAi })}
                className="mt-1 accent-accent"
              />
              <div>
                <p className="font-semibold text-sm mb-1">{c.storePhotos}</p>
                <p className="text-xs text-muted-foreground">{c.storePhotosDesc}</p>
              </div>
            </label>
            <label className="flex gap-3 p-4 rounded-2xl border border-foreground/10 cursor-pointer">
              <input
                type="checkbox"
                checked={improveAi}
                onChange={(e) => persistConsents({ storePhotos, improveAi: e.target.checked })}
                className="mt-1 accent-accent"
              />
              <div>
                <p className="font-semibold text-sm mb-1">{c.improveAi}</p>
                <p className="text-xs text-muted-foreground">{c.improveAiDesc}</p>
              </div>
            </label>
          </div>
        </div>

        <div id="retention" className="rounded-3xl border border-foreground/10 p-6 md:p-8 mb-6">
          <h2 className="font-display font-bold text-xl mb-2" style={{ fontFamily: "var(--font-display)" }}>{c.retention}</h2>
          <p className="text-sm text-muted-foreground mb-5">{c.retentionDesc}</p>

          <div className="space-y-5">
            <div>
              <label htmlFor="retention-period" className="text-sm font-semibold block mb-1.5">
                {c.retentionPeriod}
              </label>
              <select
                id="retention-period"
                value={retention.retentionDays}
                onChange={(e) => persistRetention({ retentionDays: Number(e.target.value) as RetentionDays })}
                className="w-full px-4 py-3 rounded-xl border border-foreground/20 bg-background text-sm focus:outline-none focus:ring-2 focus:ring-accent"
              >
                <option value={30}>{c.days30}</option>
                <option value={90}>{c.days90}</option>
                <option value={180}>{c.days180}</option>
                <option value={365}>{c.days365}</option>
                <option value={0}>{c.daysKeep}</option>
              </select>
              <p className="text-xs text-muted-foreground mt-1.5">{c.retentionHint}</p>
            </div>

            <label className="flex gap-3 p-4 rounded-2xl border border-foreground/10 cursor-pointer">
              <input
                type="checkbox"
                checked={retention.euOnly}
                onChange={(e) => persistRetention({ euOnly: e.target.checked })}
                className="mt-1 accent-accent"
              />
              <div>
                <p className="font-semibold text-sm mb-1">🇪🇺 {c.eu}</p>
                <p className="text-xs text-muted-foreground">{c.euDesc}</p>
              </div>
            </label>

            {retention.updatedAt && retention.updatedAt !== DEFAULT_RETENTION.updatedAt && (
              <p className="text-xs text-muted-foreground">
                {c.lastUpdated}: {new Date(retention.updatedAt).toLocaleString(locale)}
              </p>
            )}
          </div>
        </div>

        <div className="text-center space-y-3">
          <Link to="/account" className="block text-sm text-muted-foreground hover:text-foreground">
            {c.advanced}
          </Link>
          <Link to="/app" className="block text-sm text-muted-foreground hover:text-foreground">
            {c.back}
          </Link>
        </div>
      </section>
    </div>
  );
}